import { useCallback, useRef } from 'react';

import { calculateAge } from '../../utils/age';
import { useStackGeneration } from '../../contexts/StackGenerationContext';
import { requestStackAnalysis } from '../../api/analyzeStack';
import { analysisRequestBody, isSyncAnalysisPayload } from './mergeAnalysis';
import { logAnalyticsEvent } from '../../utils/analytics';

/** This flow's analytics surface. See ANALYTICS_SURFACE's doc comment. */
export const ANALYTICS_SURFACE = 'analyze_stack';

/**
 * The analysis flow's job kind in `StackGenerationContext`.
 *
 * One app-wide job slot is shared with stack generation (ruled: reuse the
 * live-progress screen). Their results have different shapes, so each page
 * ignores a job it did not start — see `kind` in the context.
 */
export const JOB_KIND = 'analyze';

// Re-exported because this hook is where the two response shapes are acted on,
// and a reader chasing "how does a cache hit get handled" arrives here first.
// It *lives* in `mergeAnalysis.js`, which imports nothing: this module reaches
// `axios` through the API service, and Jest cannot transform that, so a test
// importing the predicate from here fails to parse rather than to assert.
export { isSyncAnalysisPayload };

/**
 * Derive the member's age and gender exactly as `CreateStack` does for
 * generation (`CreateStack.js`, `handleGenerateStack`).
 *
 * Copied deliberately rather than imported: `CreateStack` computes these
 * inline inside a 240-line handler, so there is nothing to import, and the
 * ruling is that the analysis is personalised *the same way* generation is.
 * Extracting one shared helper is proposed in the PR rather than done here —
 * it would mean editing the generator's own handler, and generation behaviour
 * is to stay unchanged.
 */
export function profileDemographics(userProfile) {
  if (!userProfile) return { age: 'unknown', gender: 'unknown' };

  // **A missing or unparseable date of birth must not become `NaN`.**
  // `calculateAge` builds a `Date` from whatever it is given, so `undefined`
  // and `''` both yield `NaN` — which `JSON.stringify` then writes as `null`
  // in the request body. That is not a hypothetical shape: `useUserProfile`
  // returns `{ age: '', gender: '' }` for a member whose profile document
  // does not exist, which is a real and now-reachable state. `'unknown'` is
  // the value this function already uses for "we do not know", so an
  // incomplete profile is reported the same way an absent one is.
  const age = calculateAge(userProfile.dateOfBirth);
  return {
    age: Number.isFinite(age) ? age : 'unknown',
    gender: userProfile.gender === 'other' ? 'neutral' : (userProfile.gender || 'unknown'),
  };
}

/**
 * useStackAnalysis — start an analysis and hand the job to the shared provider.
 *
 * The hook owns the request and the job hand-off only. Consuming the finished
 * result belongs to the surface that started it (`StackForm`), because what to
 * do with it — merge, save, navigate — is that surface's business.
 *
 * A **sync** outcome (`started: true, syncResult`) means the cache already had
 * the answer: clear the 'starting' marker and let the form merge immediately,
 * mirroring `CreateStack`'s 200 branch for generation.
 *
 * @returns {{analyze: Function, isAnalyzing: boolean, inFlightRef: object}}
 */
export default function useStackAnalysis() {
  const {
    beginGenerating,
    startJob,
    clearJob,
    isInProgress,
    kind: jobKind,
    startedBy: jobStartedBy,
  } = useStackGeneration();
  // Synchronous double-submit guard. `isInProgress` is React state and will not
  // have flushed between two clicks landing in the same tick, which is the same
  // reason `CreateStack` keeps `submitInFlightRef`.
  const inFlightRef = useRef(false);

  const analyze = useCallback(
    async ({ healthGoals, supplements, userProfile, startedBy = null }) => {
      if (inFlightRef.current || isInProgress) {
        // **Whose job is in the way matters.** The provider holds one job for
        // the whole app, so a stack *generation* started at /create-stack
        // blocks an analysis too — and so does an analysis another form
        // started, e.g. on a different stack's edit page. Neither draws a
        // progress panel here, because the panel is gated on the job being
        // this form's. Saying only `in_flight` there makes the button do
        // nothing at all, with nothing on screen to explain why.
        const ownJob = jobKind === JOB_KIND && jobStartedBy === startedBy;
        return {
          started: false,
          reason: 'in_flight',
          foreignJob: isInProgress && !ownJob,
          blockedBy: jobKind,
        };
      }
      inFlightRef.current = true;

      const { age, gender } = profileDemographics(userProfile);

      logAnalyticsEvent('analyze_stack', {
        surface: ANALYTICS_SURFACE,
        health_goals: healthGoals,
        number_of_supplements: (supplements || []).filter((s) => s?.selected !== false).length,
        supplement_ids: (supplements || []).map((s) => s?.supplementId).filter(Boolean),
      });

      try {
        // Record 'starting' BEFORE the POST, as generation does: on a cold
        // backend that request can take many seconds, and the live-progress
        // screen has to be up for all of it rather than appearing once a job id
        // comes back.
        beginGenerating(healthGoals, JOB_KIND, startedBy);

        const { status, data } = await requestStackAnalysis(
          analysisRequestBody({ healthGoals, supplements, age, gender })
        );

        if (isSyncAnalysisPayload(status, data)) {
          // Cache hit with a finished body — same path CreateStack takes on 200.
          clearJob();
          return { started: true, syncResult: data };
        }

        const jobId = data?.job_id;
        if (jobId) {
          startJob(jobId, healthGoals, JOB_KIND, startedBy);
          return { started: true, jobId };
        }

        clearJob();
        return { started: false, reason: 'no_job_id' };
      } catch (error) {
        clearJob();
        return { started: false, reason: 'request_failed', error };
      } finally {
        inFlightRef.current = false;
      }
    },
    [beginGenerating, startJob, clearJob, isInProgress, jobKind, jobStartedBy]
  );

  return { analyze, isAnalyzing: isInProgress, inFlightRef };
}
